import React, { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import TextButton from 'components/TextButton/TextButton';
import type { DpiaHistoryListItem } from 'config/api/dpiaRequests/dpiaRequests.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import { getDpiaDecisionLabel } from './apaDpiaStatus.helpers';

export const sortDpiaHistoryItems = (items: DpiaHistoryListItem[]): DpiaHistoryListItem[] =>
  [...items].sort((left, right) => {
    if (left.isCurrent !== right.isCurrent) {
      return left.isCurrent ? -1 : 1;
    }

    return (right.createdAt || '').localeCompare(left.createdAt || '');
  });

const formatHistoryDate = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toISOString().slice(0, 10);
};

type DpiaStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_dpia.statuses;

type Props = {
  items: DpiaHistoryListItem[];
  onOpenItem?: (item: DpiaHistoryListItem) => void;
};

const DpiaHistoryList: React.FC<Props> = ({ items, onOpenItem }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.dpia_history;
  const statusMessages = processing_activities_messages.asset_ropa.owner_dpia.statuses;
  const sorted = useMemo(() => sortDpiaHistoryItems(items), [items]);

  if (sorted.length === 0) {
    return (
      <Typography color='textSecondary' variant='body2'>
        {t(messages.empty)}
      </Typography>
    );
  }

  return (
    <>
      {sorted.map(item => {
        const statusKey = item.status as DpiaStatusMessageKey;
        const statusLabel = statusMessages[statusKey] ? t(statusMessages[statusKey]) : item.status.replace(/_/g, ' ');
        const decisionLabel = item.decision ? getDpiaDecisionLabel(item.decision, t) : null;
        const date = formatHistoryDate(item.approvedAt || item.createdAt);
        const title = item.title || t(messages.untitled);

        return (
          <Box key={item.id} style={{ marginTop: 8 }}>
            {onOpenItem ? (
              <TextButton onClick={() => onOpenItem(item)}>
                <Typography variant='body2'>
                  <strong>{title}</strong>
                </Typography>
              </TextButton>
            ) : (
              <Typography variant='body2'>
                <strong>{title}</strong>
              </Typography>
            )}
            <Typography color='textSecondary' variant='body2'>
              {statusLabel}
              {decisionLabel ? ` · ${decisionLabel}` : ''}
              {date ? ` · ${date}` : ''}
              {item.isCurrent && (
                <span className={clsx(styles.statusBadge, styles.statusBadgeSuccess)} style={{ marginLeft: 8 }}>
                  {t(messages.current)}
                </span>
              )}
            </Typography>
          </Box>
        );
      })}
    </>
  );
};

export default DpiaHistoryList;
